import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import InvoiceView from '../components/invoice/InvoiceView';
import QuoteView from '../components/quote/QuoteView';
import POView from '../components/poManagement/POView';
import Pagination from '../components/common/Pagination';
import backButtonIcon from '../assets/backButton.png';
import { format, parseISO } from 'date-fns';
import { API_URL, ENDPOINTS, AUTH_TOKEN_KEY } from '../config';
import PageLoadingSpinner from '../components/ui/PageLoadingSpinner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useStoreFilter, addStoreFilterToParams } from '../hooks/useStoreFilter';
import { useAuth } from '../context/AuthContext';

const PageHeader = styled.div`
  padding: 2rem 2rem 3rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  color: #042B41;
  font-weight: 800;
  margin: 0;
`;

// Updated header container to match QuotesPage layout
const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.5rem;
`;

// Create TitleContainer similar to QuotesPage
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Style for back button without text
const StyledBackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #042B41;
`;

const BackIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 8px;
`;

// Update filter container to use flexbox and align items
const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
  width: 100%;
`;

// Match the TabContainer from QuotesPage
const TabContainer = styled.div`
  display: flex;
  width: 485px;
  height: 44px;
  border-radius: 10px;
  border: 0.5px solid #E5E7EB;
  overflow: hidden;
`;

// Match the Tab from QuotesPage
const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px;
  background: ${props => props.active ? '#042B41' : 'white'};
  color: ${props => props.active ? 'white' : '#6B7280'};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '500'};
  border: none;
  transition: all 0.2s ease-in-out;
  
  &:not(:last-child) {
    border-right: 0.5px solid #E5E7EB;
  }
  
  &:hover {
    background-color: ${props => props.active ? '#042B41' : '#f8f8f8'};
    color: ${props => props.active ? 'white' : '#042B41'};
  }
`;

// Update search container to match quotes page style
const SearchContainer = styled.div`
  position: relative;
  width: 240px;
  height: 42px;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 10px 35px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

// Update filter dropdown to match other elements height
const FilterDropdown = styled.select`
  padding: 0.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  height: 42px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

// Add store type dropdown styling
const StoreTypeDropdown = styled.select`
  padding: 0.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  height: 42px;
  cursor: pointer;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

// Modify download button to push it to the right
const DownloadButton = styled.button`
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  height: 42px;
  transition: background-color 0.2s;
  margin-left: auto;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    background-color: #031f30;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  
  th {
    padding: 10px 12px;
    text-align: left;
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
    vertical-align: middle;
    height: 48px;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #e0e0e0;
    height: 48px;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background-color: #f9fafb;
    }
  }
  
  td {
    padding: 10px 12px;
    color: #333;
    text-align: left;
    font-size: 14px;
    vertical-align: middle;
    border: 1px solid #e0e0e0;
    height: 48px;
    box-sizing: border-box;
  }
`;

const InvoiceLink = styled.a`
  color: #1D4ED8;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  display: inline-block;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1E40AF;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
  }
  
  &:active {
    color: #1E3A8A;
  }
`;

const POLink = styled.a`
  color: #1D4ED8;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  display: inline-block;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1E40AF;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
  }
  
  &:active {
    color: #1E3A8A;
  }
`;

// Add QuoteLink component following the same pattern
const QuoteLink = styled.a`
  color: #1D4ED8;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  display: inline-block;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1E40AF;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
  }
  
  &:active {
    color: #1E3A8A;
  }
`;

// Loading and error states
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #6B7280;
  
  h3 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    color: #374151;
  }
  
  p {
    margin-bottom: 0;
    max-width: 300px;
    text-align: center;
  }
`;

const ErrorContainer = styled.div`
  background-color: #FEF2F2;
  border: 1px solid #FCA5A5;
  color: #B91C1C;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
`;

// Add a section title that updates based on active tab
const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #042B41;
  font-weight: 600;
  margin: 1.5rem 0;
`;

// Remove skeleton components and add consistent loading components
const LoadingRow = styled.tr`
  td {
    text-align: center !important;
    padding: 40px 20px !important;
    border: none !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
`;

// Define a type for invoice data
interface SalesReport {
  id: string;
  date: string;
  invoiceNumber: string;
  companyName: string;
  amount: string; // Formatted display value
  rawAmount: number; // Raw numeric value for CSV export
  paymentMode: string;
  rawPaymentMode: string; // Raw value from API for CSV export
  storeType: string; // New field for store type
}

interface POReport {
  id: string | number;
  date: string;
  poNumber: string;
  supplierName: string;
  invoiceNumber: string;
  amount: string; // Formatted display value
  rawAmount: number; // Raw numeric value for CSV export
  storeType: string; // New field for store type
}

interface QuoteReport {
  id: string;
  date: string;
  quoteNumber: string;
  companyName: string;
  grandTotal: string; // Formatted display value
  rawGrandTotal: number; // Raw numeric value for CSV export
  storeType: string; // New field for store type
}

type Report = SalesReport | POReport | QuoteReport;

const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to get initial active filter from URL params or localStorage
  const getInitialActiveFilter = (): 'sales' | 'po' | 'quotes' => {
    // First, try to get from URL search parameters
    const tabParam = searchParams.get('tab');
    if (tabParam === 'sales' || tabParam === 'po' || tabParam === 'quotes') {
      return tabParam;
    }

    // Fallback to localStorage for backward compatibility
    const savedTab = localStorage.getItem('reportsActiveTab');
    if (savedTab === 'sales' || savedTab === 'po' || savedTab === 'quotes') {
      return savedTab;
    }

    // Default to 'sales' if nothing is found
    return 'sales';
  };

  // State management
  const [activeFilter, setActiveFilter] = useState<'sales' | 'po' | 'quotes'>(getInitialActiveFilter());
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [viewingQuote, setViewingQuote] = useState(false);
  const [selectedPOId, setSelectedPOId] = useState<string | null>(null);
  const [viewingPO, setViewingPO] = useState(false);
  const [salesReports, setSalesReports] = useState<SalesReport[]>([]);
  const [poReports, setPOReports] = useState<POReport[]>([]);
  const [quoteReports, setQuoteReports] = useState<QuoteReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataFetchInProgress, setDataFetchInProgress] = useState<{ sales: boolean, po: boolean, quotes: boolean }>({ sales: false, po: false, quotes: false });

  // Add store type filter state
  const [storeTypeFilter, setStoreTypeFilter] = useState<string>('All');

  const navigate = useNavigate();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const apiCallInProgress = useRef<boolean>(false);
  
  // Add store filtering support - memoize to prevent infinite re-renders
  const storeFilterHook = useStoreFilter();
  const { user } = useAuth();
  const storeFilter = useMemo(() => ({
    shouldFilterByStore: storeFilterHook.shouldFilterByStore,
    userStoreType: storeFilterHook.userStoreType,
    userStoreTypeId: storeFilterHook.userStoreTypeId,
    isStoreRestricted: storeFilterHook.isStoreRestricted
  }), [
    storeFilterHook.shouldFilterByStore,
    storeFilterHook.userStoreType,
    storeFilterHook.userStoreTypeId,
    storeFilterHook.isStoreRestricted
  ]);

  // Helper function to check authentication status
  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      console.error('No authentication token found');
      setError('Authentication token missing. Please log in again.');
      return false;
    }
    return true;
  }, []);

  // Calculate pagination values
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Sync URL parameters with initial active filter on component mount
  useEffect(() => {
    const currentTabParam = searchParams.get('tab');
    if (currentTabParam !== activeFilter) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', activeFilter);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, []); // Only run once on mount

  // Reset loading state when active filter changes to prevent blinking
  useEffect(() => {
    // Reset loading state and data fetch flags
    setIsLoading(false);
    setDataFetchInProgress({ sales: false, po: false, quotes: false });
    apiCallInProgress.current = false;
    
    // Clear any previous errors
    setError(null);
  }, [activeFilter]);

  // Implement search term debounce
  useEffect(() => {
    console.log('Search term changed:', searchTerm);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      console.log('Setting debounced search term:', searchTerm);
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Use useEffect with proper dependencies
  useEffect(() => {
    // Create a unique identifier for this effect execution
    const effectId = Date.now();
    console.log(`[${effectId}] Effect triggered with:`, { activeFilter, currentPage, debouncedSearchTerm });
    
    // Ensure we're not in the middle of another API call
    if (apiCallInProgress.current) {
      console.log(`[${effectId}] Skipping API call - another call is in progress`);
      return;
    }
    
    // Reset any previous errors
    setError(null);
    
    // Use a ref to track if this effect instance has been superseded
    const isCancelled = { current: false };
    
    // Add a small delay to ensure state updates have propagated
    const timer = setTimeout(() => {
      // Check if this effect has been cancelled
      if (isCancelled.current) {
        console.log(`[${effectId}] Effect cancelled before execution`);
        return;
      }
      
      console.log(`[${effectId}] Executing API call for ${activeFilter}`);
      
      if (activeFilter === 'sales') {
        fetchSalesReports();
      } else if (activeFilter === 'po') {
        fetchPOReports();
      } else if (activeFilter === 'quotes') {
        fetchQuoteReports();
      }
    }, 100);
    
    // Add a safety timeout to prevent getting stuck in loading state
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.log(`[${effectId}] Safety timeout triggered - resetting loading state`);
        setIsLoading(false);
        setDataFetchInProgress({ sales: false, po: false, quotes: false });
        apiCallInProgress.current = false;
        setError('Request timed out. Please try again.');
      }
    }, 10000); // 10 seconds timeout
    
    // Cleanup function
    return () => {
      console.log(`[${effectId}] Cleaning up effect`);
      isCancelled.current = true;
      clearTimeout(timer);
      clearTimeout(safetyTimeout);
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, currentPage, itemsPerPage, debouncedSearchTerm, storeTypeFilter]);

  // Fetch sales report data with useCallback to prevent duplicate API calls
  const fetchSalesReports = useCallback(async () => {
    console.log('fetchSalesReports called');
    
    // Return early if already loading or fetch in progress
    if (apiCallInProgress.current || dataFetchInProgress.sales) {
      console.log('Skipping fetchSalesReports - already in progress');
      return;
    }

    // Check authentication first
    if (!checkAuthentication()) {
      console.log('Authentication check failed');
      setIsLoading(false);
      setDataFetchInProgress(prev => ({ ...prev, sales: false }));
      return;
    }

    setDataFetchInProgress(prev => ({ ...prev, sales: true }));
    setIsLoading(true);
    setError(null);
    apiCallInProgress.current = true;

    try {
      // Get the token for authentication
      const token = localStorage.getItem(AUTH_TOKEN_KEY);

      // Use the reports API endpoint for sales data with proper pagination and store filtering
      let queryParams = new URLSearchParams();
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());
      if (debouncedSearchTerm && debouncedSearchTerm.trim() !== '') {
        queryParams.append('search', debouncedSearchTerm.trim());
      }
      
      // Add store type filter if not "All"
      if (storeTypeFilter && storeTypeFilter !== 'All') {
        queryParams.append('store_type', storeTypeFilter);
      }
      
      // Apply store-based filtering for staff/manager users (but not override manual filter)
      if (storeFilter && storeFilter.shouldFilterByStore && storeTypeFilter === 'All') {
        queryParams = addStoreFilterToParams(queryParams, storeFilter);
      }
      
      const endpoint = `${API_URL}/api/v1/reports/sales?${queryParams.toString()}`;

      console.log('Fetching sales report data from:', endpoint);

      // Make a direct fetch request with proper headers
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.items)) {
        throw new Error('Invalid response format from server');
      }

      // Map the sales data from the reports API
      const formattedSalesData: SalesReport[] = data.items.map((sale: any) => ({
        id: sale.invoice_number || `SALE-${Date.now()}`, // Use invoice_number as ID
        date: formatDate(sale.date),
        invoiceNumber: sale.invoice_number || 'N/A',
        companyName: sale.company_name || 'N/A',
        amount: formatCurrency(sale.total_amount),
        rawAmount: typeof sale.total_amount === 'number' ? sale.total_amount : parseFloat(sale.total_amount) || 0,
        paymentMode: formatPaymentMode(sale.mode_of_payment),
        rawPaymentMode: sale.mode_of_payment || 'N/A',
        storeType: sale.store_type || 'N/A' // Add store type
      }));

      setSalesReports(formattedSalesData);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Error fetching sales reports:', error);
      setError('Failed to load sales report data. ' + (error instanceof Error ? error.message : ''));
      setSalesReports([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
      setDataFetchInProgress(prev => ({ ...prev, sales: false }));
      apiCallInProgress.current = false;
      console.log('fetchSalesReports completed');
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, storeTypeFilter, checkAuthentication]);

  // Fetch purchase order report data with useCallback to prevent duplicate API calls
  const fetchPOReports = useCallback(async () => {
    console.log('fetchPOReports called with:', { 
      currentPage, 
      itemsPerPage, 
      debouncedSearchTerm,
      apiCallInProgress: apiCallInProgress.current,
      dataFetchInProgress: dataFetchInProgress.po
    });
    
    // Return early if already loading or fetch in progress
    if (apiCallInProgress.current || dataFetchInProgress.po) {
      console.log('Skipping fetchPOReports - already in progress');
      return;
    }

    // Check authentication first
    if (!checkAuthentication()) {
      console.log('Authentication check failed');
      setIsLoading(false);
      setDataFetchInProgress(prev => ({ ...prev, po: false }));
      return;
    }

    setDataFetchInProgress(prev => ({ ...prev, po: true }));
    setIsLoading(true);
    setError(null);
    apiCallInProgress.current = true;

    try {
      // Get the token for authentication
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      
      // Check if API_URL is defined
      if (!API_URL) {
        console.error('API_URL is not defined');
        throw new Error('API configuration error. Please contact support.');
      }
      
      // Use the reports API endpoint for purchase orders with store filtering
      let queryParams = new URLSearchParams();
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());
      
      // Ensure search term is properly encoded and added to query params
      if (debouncedSearchTerm && debouncedSearchTerm.trim() !== '') {
        console.log('Adding search term to query:', debouncedSearchTerm);
        queryParams.append('search', debouncedSearchTerm.trim());
      }
      
      // Add store type filter if not "All"
      if (storeTypeFilter && storeTypeFilter !== 'All') {
        queryParams.append('store_type', storeTypeFilter);
      }
      
      // Apply store-based filtering for staff/manager users (but not override manual filter)
      if (storeFilter && storeFilter.shouldFilterByStore && storeTypeFilter === 'All') {
        queryParams = addStoreFilterToParams(queryParams, storeFilter);
      }
      
      // Use the endpoint from config
      const endpoint = `${API_URL}${ENDPOINTS.REPORTS_PURCHASE_ORDERS}?${queryParams.toString()}`;

      console.log('Fetching PO report data from:', endpoint);

      // Make a direct fetch request with proper headers
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        mode: 'cors'
      });

      console.log('API response status:', response.status);

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('PO API response:', data);

      if (!data || !Array.isArray(data.items)) {
        throw new Error('Invalid response format from server');
      }

      // Format the PO data from the reports API
      const formattedPOData: POReport[] = data.items.map((po: any) => ({
        id: po.id || po.po_number || `PO-${Date.now()}`, // Use numeric id first, fallback to po_number
        date: formatDate(po.date),
        poNumber: po.po_number || 'N/A',
        supplierName: po.supplier_name || 'N/A',
        invoiceNumber: po.linked_invoice_number || 'N/A', // Use the linked_invoice_number from API
        amount: formatCurrency(po.total_amount),
        rawAmount: typeof po.total_amount === 'number' ? po.total_amount : parseFloat(po.total_amount) || 0,
        storeType: po.store_type || 'N/A' // Add store type
      }));

      console.log('Formatted PO data:', formattedPOData);
      setPOReports(formattedPOData);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Error fetching PO reports:', error);
      setError('Failed to load purchase order report data. ' + (error instanceof Error ? error.message : ''));
      setPOReports([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
      setDataFetchInProgress(prev => ({ ...prev, po: false }));
      apiCallInProgress.current = false;
      console.log('fetchPOReports completed');
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, storeTypeFilter, checkAuthentication]);

  // Fetch quote report data with useCallback to prevent duplicate API calls
  const fetchQuoteReports = useCallback(async () => {
    console.log('fetchQuoteReports called');
    
    // Return early if already loading or fetch in progress
    if (apiCallInProgress.current || dataFetchInProgress.quotes) {
      console.log('Skipping fetchQuoteReports - already in progress');
      return;
    }

    // Check authentication first
    if (!checkAuthentication()) {
      console.log('Authentication check failed');
      setIsLoading(false);
      setDataFetchInProgress(prev => ({ ...prev, quotes: false }));
      return;
    }

    setDataFetchInProgress(prev => ({ ...prev, quotes: true }));
    setIsLoading(true);
    setError(null);
    apiCallInProgress.current = true;

    try {
      // Get the token for authentication
      const token = localStorage.getItem(AUTH_TOKEN_KEY);

      // Use the reports API endpoint for quotes with store filtering
      let queryParams = new URLSearchParams();
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());
      if (debouncedSearchTerm && debouncedSearchTerm.trim() !== '') {
        queryParams.append('search', debouncedSearchTerm.trim());
      }
      
      // Add store type filter if not "All"
      if (storeTypeFilter && storeTypeFilter !== 'All') {
        queryParams.append('store_type', storeTypeFilter);
      }
      
      // Apply store-based filtering for staff/manager users (but not override manual filter)
      if (storeFilter && storeFilter.shouldFilterByStore && storeTypeFilter === 'All') {
        queryParams = addStoreFilterToParams(queryParams, storeFilter);
      }
      
      const endpoint = `${API_URL}${ENDPOINTS.QUOTES_REPORTS}?${queryParams.toString()}`;

      console.log('Fetching quotes report data from:', endpoint);

      // Make a direct fetch request with proper headers
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        mode: 'cors'
      });

      console.log('API response status:', response.status);

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Quote API response:', data);

      if (!data || !Array.isArray(data.items)) {
        throw new Error('Invalid response format from server');
      }

      // Format the quote data from the reports API
      const formattedQuoteData: QuoteReport[] = data.items.map((quote: any) => ({
        id: quote.id || quote.quote_number || `QUOTE-${Date.now()}`,
        date: formatDate(quote.date),
        quoteNumber: quote.quote_number || 'N/A',
        companyName: quote.company_name || 'N/A',
        grandTotal: formatCurrency(quote.grand_total),
        rawGrandTotal: typeof quote.grand_total === 'number' ? quote.grand_total : parseFloat(quote.grand_total) || 0,
        storeType: quote.store_type || 'N/A' // Add store type
      }));

      console.log('Formatted Quote data:', formattedQuoteData);
      setQuoteReports(formattedQuoteData);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Error fetching quote reports:', error);
      setError('Failed to load quote report data. ' + (error instanceof Error ? error.message : ''));
      setQuoteReports([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
      setDataFetchInProgress(prev => ({ ...prev, quotes: false }));
      apiCallInProgress.current = false;
      console.log('fetchQuoteReports completed');
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, storeTypeFilter, checkAuthentication]);

  // Helper to format dates from API
  const formatDate = (dateStr: string): string => {
    try {
      if (!dateStr) return 'N/A';
      const date = parseISO(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch (err) {
      console.error('Error formatting date:', err);
      return dateStr || 'N/A';
    }
  };

  // Helper to format currency
  const formatCurrency = (amount: number | string): string => {
    if (amount === undefined || amount === null) return '$0.00';

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    return `$${numAmount.toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Helper to format payment mode
  const formatPaymentMode = (mode?: string): string => {
    if (!mode) return 'N/A';

    // Handle malformed enum strings like "PaymentMode.credit_card"
    if (mode.includes('.')) {
      mode = mode.split('.').pop() || mode;
    }

    // Convert snake_case to Title Case
    return mode
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Navigation functions
  const handleBack = () => {
    navigate('/home');
  };

  const handleFilterChange = (filter: 'sales' | 'po' | 'quotes') => {
    if (filter !== activeFilter) {
      // Save the selected filter to localStorage to persist across page refreshes
      localStorage.setItem('reportsActiveTab', filter);
      
      // Update URL parameters to persist tab state
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', filter);
      setSearchParams(newSearchParams);
      
      setActiveFilter(filter);
      setCurrentPage(1); // Reset to first page
      setSearchTerm(''); // Clear search
      setStoreTypeFilter('All'); // Reset store type filter
      setError(null); // Clear any error messages
      setSelectedInvoiceId(null); // Clear any selected invoice
      setViewingInvoice(false); // Exit invoice viewing mode
      setSelectedQuoteId(null); // Clear any selected quote
      setViewingQuote(false); // Exit quote viewing mode
      setSelectedPOId(null); // Clear any selected PO
      setViewingPO(false); // Exit PO viewing mode
      setDataFetchInProgress({ sales: false, po: false, quotes: false }); // Reset data fetched flag
      // Reset data for previous filter
      if (filter === 'sales') {
        setPOReports([]); // Clear PO reports when switching to sales
        setQuoteReports([]); // Clear quote reports when switching to sales
      } else if (filter === 'po') {
        setSalesReports([]); // Clear sales reports when switching to PO
        setQuoteReports([]); // Clear quote reports when switching to PO
      } else if (filter === 'quotes') {
        setSalesReports([]); // Clear sales reports when switching to quotes
        setPOReports([]); // Clear PO reports when switching to quotes
      }
    }
  };

  // Add handler for store type filter change
  const handleStoreTypeFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStoreTypeFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // CSV export function
  const handleDownloadCSV = () => {
    let data;
    if (activeFilter === 'sales') {
      data = salesReports;
    } else if (activeFilter === 'po') {
      data = poReports;
    } else {
      data = quoteReports;
    }

    if (data.length === 0) return;

    let csvContent = '';

    // Add headers
    if (activeFilter === 'sales') {
      csvContent = 'Date,Invoice Number,Company Name,Total Amount (AUD),Payment Mode,Store Type\n';

      // Add data rows for sales - use raw numeric values for proper CSV formatting
      (data as SalesReport[]).forEach(item => {
        const amount = item.rawAmount !== undefined ? item.rawAmount.toFixed(2) : '0.00';
        const paymentMode = item.rawPaymentMode || item.paymentMode || 'N/A';
        csvContent += `${item.date},${item.invoiceNumber},"${item.companyName}",${amount},${paymentMode},${item.storeType}\n`;
      });
    } else if (activeFilter === 'po') {
      csvContent = 'Date,PO Number,Supplier Name,Linked Invoice,Total Amount (AUD),Store Type\n';

      // Add data rows for PO - use raw numeric values for proper CSV formatting
      (data as POReport[]).forEach(item => {
        const amount = item.rawAmount !== undefined ? item.rawAmount.toFixed(2) : '0.00';
        csvContent += `${item.date},${item.poNumber},"${item.supplierName}",${item.invoiceNumber},${amount},${item.storeType}\n`;
      });
    } else {
      csvContent = 'Date,Quote Number,Company Name,Grand Total (AUD),Store Type\n';

      // Add data rows for quotes - use raw numeric values for proper CSV formatting
      (data as QuoteReport[]).forEach(item => {
        const grandTotal = item.rawGrandTotal !== undefined ? item.rawGrandTotal.toFixed(2) : '0.00';
        csvContent += `${item.date},${item.quoteNumber},"${item.companyName}",${grandTotal},${item.storeType}\n`;
      });
    }

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${activeFilter === 'sales' ? 'sales' : activeFilter === 'po' ? 'purchase_orders' : 'quotes'}_report.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewInvoice = (invoiceId: string) => {
    console.log('Opening invoice modal for ID:', invoiceId);
    console.log('Current viewingInvoice state:', viewingInvoice);
    console.log('Current selectedInvoiceId state:', selectedInvoiceId);

    // Close any existing modal first to ensure clean state
    if (viewingInvoice) {
      setViewingInvoice(false);
      setSelectedInvoiceId(null);

      // Small delay to ensure the previous modal is fully unmounted
      setTimeout(() => {
        setSelectedInvoiceId(invoiceId);
        setViewingInvoice(true);
        console.log('Invoice modal opened with ID:', invoiceId);
      }, 100);
    } else {
      setSelectedInvoiceId(invoiceId);
      setViewingInvoice(true);
      console.log('Invoice modal opened with ID:', invoiceId);
    }
  };

  const handleCloseInvoice = () => {
    console.log('Closing invoice modal');
    setViewingInvoice(false);
    setSelectedInvoiceId(null);
    console.log('Invoice modal closed');
  };

  const handleViewQuote = (quoteId: string) => {
    console.log('Opening quote modal for ID:', quoteId);
    console.log('Current viewingQuote state:', viewingQuote);
    console.log('Current selectedQuoteId state:', selectedQuoteId);

    // Close any existing modal first to ensure clean state
    if (viewingQuote) {
      setViewingQuote(false);
      setSelectedQuoteId(null);

      // Small delay to ensure the previous modal is fully unmounted
      setTimeout(() => {
        setSelectedQuoteId(quoteId);
        setViewingQuote(true);
        console.log('Quote modal opened with ID:', quoteId);
      }, 100);
    } else {
      setSelectedQuoteId(quoteId);
      setViewingQuote(true);
      console.log('Quote modal opened with ID:', quoteId);
    }
  };

  const handleCloseQuote = () => {
    console.log('Closing quote modal');
    setViewingQuote(false);
    setSelectedQuoteId(null);
    console.log('Quote modal closed');
  };

  const handleViewPO = (poId: string) => {
    console.log('Viewing PO with ID:', poId);
    
    let validPoId = poId;
    
    // Try to extract numeric ID from PO number if it's in format "PO-12345"
    if (typeof poId === 'string' && poId.startsWith('PO-')) {
      const numericPart = poId.replace('PO-', '');
      const numericId = parseInt(numericPart);
      if (!isNaN(numericId) && numericId > 0) {
        validPoId = numericId.toString();
        console.log('Extracted numeric ID from PO number:', validPoId);
      } else {
        console.error('Could not extract valid numeric ID from PO number:', poId);
        setError(`Invalid PO format: ${poId}. Cannot open PO view.`);
        return;
      }
    } else {
      // Check if it's already a valid numeric ID
      const numericId = parseInt(poId);
      if (isNaN(numericId) || numericId <= 0) {
        console.error('Invalid PO ID:', poId);
        setError(`Invalid PO ID: ${poId}. Cannot open PO view.`);
        return;
      }
    }
    
    setSelectedPOId(validPoId);
    setViewingPO(true);
  };

  const handleClosePO = () => {
    console.log('Closing PO modal');
    setViewingPO(false);
    setSelectedPOId(null);
    console.log('PO modal closed');
  };

  // Type guards for the Report union type
  const isSalesReport = (report: Report): report is SalesReport => {
    return (report as SalesReport).paymentMode !== undefined;
  };

  const isPOReport = (report: Report): report is POReport => {
    return (report as POReport).poNumber !== undefined;
  };

  const isQuoteReport = (report: Report): report is QuoteReport => {
    return (report as QuoteReport).quoteNumber !== undefined;
  };

  // Render table content with consistent loading pattern
  const renderTableContent = () => {
    // Check if we have data to display
    const hasData = (activeFilter === 'sales' && salesReports.length > 0) ||
                   (activeFilter === 'po' && poReports.length > 0) ||
                   (activeFilter === 'quotes' && quoteReports.length > 0);
                   
    // If we have data, show it even if loading is true (prevents blinking during re-fetches)
    if (hasData && !error) {
      if (activeFilter === 'sales') {
        return salesReports.map(report => (
          <tr key={report.id}>
            <td>{report.date}</td>
            <td>
              <InvoiceLink onClick={() => handleViewInvoice(report.id)}>
                {report.invoiceNumber}
              </InvoiceLink>
            </td>
            <td>{report.companyName}</td>
            <td>{report.storeType || 'N/A'}</td>
            <td style={{ textAlign: 'right' }}>{report.amount}</td>
            <td>{report.paymentMode}</td>
          </tr>
        ));
      }

      if (activeFilter === 'po') {
        return poReports.map(report => (
          <tr key={report.id}>
            <td>{report.date}</td>
            <td>
              <POLink onClick={() => handleViewPO(report.id.toString())}>
                {report.poNumber}
              </POLink>
            </td>
            <td>{report.supplierName}</td>
            <td>{report.storeType || 'N/A'}</td>
            <td>{report.invoiceNumber}</td>
            <td style={{ textAlign: 'right' }}>{report.amount}</td>
          </tr>
        ));
      }

      if (activeFilter === 'quotes') {
        return quoteReports.map(report => (
          <tr key={report.id}>
            <td>{report.date}</td>
            <td>
              <QuoteLink onClick={() => handleViewQuote(report.id)}>
                {report.quoteNumber}
              </QuoteLink>
            </td>
            <td>{report.companyName}</td>
            <td>{report.storeType || 'N/A'}</td>
            <td style={{ textAlign: 'right' }}>{report.grandTotal}</td>
          </tr>
        ));
      }
    }
    
    // Show loading state only if we don't have data or if there's an error
    if (isLoading) {
      return (
        <LoadingRow>
          <td colSpan={activeFilter === 'quotes' ? 5 : 6}>
            <LoadingContainer>
              <LoadingSpinner size="lg" />
              <LoadingText>Loading {activeFilter === 'sales' ? 'sales reports' : activeFilter === 'po' ? 'purchase order reports' : 'quote reports'}...</LoadingText>
            </LoadingContainer>
          </td>
        </LoadingRow>
      );
    }

    if (error) {
      return (
        <tr>
          <td colSpan={activeFilter === 'quotes' ? 5 : 6} style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#e53e3e' }}>{error}</p>
          </td>
        </tr>
      );
    }

    // Empty state
    return (
      <tr>
        <td colSpan={activeFilter === 'quotes' ? 5 : 6} style={{ textAlign: 'center', padding: '2rem' }}>
          {activeFilter === 'sales' ? (
            <>
              <h3 style={{ margin: '0 0 0.5rem' }}>No Sales Data</h3>
              <p style={{ margin: 0 }}>No sales data found for the selected filters.</p>
            </>
          ) : activeFilter === 'po' ? (
            <>
              <h3 style={{ margin: '0 0 0.5rem' }}>No Purchase Order Data</h3>
              <p style={{ margin: 0 }}>No purchase order data found for the selected filters.</p>
            </>
          ) : (
            <>
              <h3 style={{ margin: '0 0 0.5rem' }}>No Quote Data</h3>
              <p style={{ margin: 0 }}>No quote data found for the selected filters.</p>
            </>
          )}
        </td>
      </tr>
    );
  };

  // Render the appropriate table based on filter
  const renderTable = () => {
    return (
      <TableContainer>
        <Table>
          <TableHeader>
            {activeFilter === 'sales' ? (
              <tr>
                <th style={{ width: '12%' }}>Date</th>
                <th style={{ width: '18%' }}>Invoice Number</th>
                <th style={{ width: '25%' }}>Company Name</th>
                <th style={{ width: '15%' }}>Store Type</th>
                <th style={{ width: '15%' }}>Total Amount (AUD)</th>
                <th style={{ width: '15%' }}>Mode of Payment</th>
              </tr>
            ) : activeFilter === 'po' ? (
              <tr>
                <th style={{ width: '12%' }}>Date</th>
                <th style={{ width: '18%' }}>PO Number</th>
                <th style={{ width: '25%' }}>Supplier Name</th>
                <th style={{ width: '15%' }}>Store Type</th>
                <th style={{ width: '15%' }}>Linked to Invoice</th>
                <th style={{ width: '15%' }}>Total Amount (AUD)</th>
              </tr>
            ) : (
              <tr>
                <th style={{ width: '15%' }}>Date</th>
                <th style={{ width: '20%' }}>Quote Number</th>
                <th style={{ width: '30%' }}>Company Name</th>
                <th style={{ width: '15%' }}>Store Type</th>
                <th style={{ width: '20%' }}>Grand Total (AUD)</th>
              </tr>
            )}
          </TableHeader>
          <TableBody>
            {renderTableContent()}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Helper to determine search placeholder
  const getSearchPlaceholder = () => {
    return activeFilter === 'sales'
      ? 'Search invoices'
      : activeFilter === 'po'
        ? 'Search Purchase Orders'
        : 'Search Quotes';
  };

  // Pagination handlers
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber !== currentPage) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0); // Scroll back to top on page change

      // Data will be re-fetched via the useEffect that depends on currentPage
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <Layout>
      <PageHeader>
        <HeaderContainer>
          <TitleContainer>
            <StyledBackButton onClick={handleBack}>
              <BackIcon src={backButtonIcon} alt="Back" />
            </StyledBackButton>
            <PageTitle>Reports</PageTitle>
          </TitleContainer>
        </HeaderContainer>

        <FilterContainer>
          <TabContainer>
            <TabButton
              active={activeFilter === 'sales'}
              onClick={() => handleFilterChange('sales')}
            >
              Sales Reports
            </TabButton>
            <TabButton
              active={activeFilter === 'po'}
              onClick={() => handleFilterChange('po')}
            >
              Purchase Orders
            </TabButton>
            <TabButton
              active={activeFilter === 'quotes'}
              onClick={() => handleFilterChange('quotes')}
            >
              Quotes
            </TabButton>
          </TabContainer>

          <SearchContainer>
            <SearchIconWrapper>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </SearchIconWrapper>
            <SearchInput
              type="text"
              placeholder={getSearchPlaceholder()}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          {(user?.role === 'admin' || (user?.role === 'manager' && ((user as any)?.can_access_all_stores || (user as any)?.canAccessAllStores))) && (
            <StoreTypeDropdown
              value={storeTypeFilter}
              onChange={handleStoreTypeFilterChange}
            >
              <option value="All">All</option>
              <option value="Cranbourne">Cranbourne</option>
              <option value="Sale">Sale</option>
            </StoreTypeDropdown>
          )}

          <DownloadButton
            onClick={handleDownloadCSV}
            disabled={isLoading || (activeFilter === 'sales' ? salesReports.length === 0 : activeFilter === 'po' ? poReports.length === 0 : quoteReports.length === 0)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download as CSV
          </DownloadButton>
        </FilterContainer>

        <SectionTitle>
          {activeFilter === 'sales' ? 'Sales Reports' : activeFilter === 'po' ? 'Purchase Order Reports' : 'Quote Reports'}
        </SectionTitle>

        {renderTable()}

        {(activeFilter === 'sales' ? salesReports.length > 0 : activeFilter === 'po' ? poReports.length > 0 : quoteReports.length > 0) && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 30]}
            showItemsPerPage={true}
          />
        )}

        {viewingInvoice && selectedInvoiceId && (
          <InvoiceView
            invoiceId={selectedInvoiceId}
            onClose={handleCloseInvoice}
          />
        )}

        {viewingQuote && selectedQuoteId && (
          <QuoteView
            quoteId={selectedQuoteId}
            onClose={handleCloseQuote}
          />
        )}

        {viewingPO && selectedPOId && (
          <POView
            poId={selectedPOId}
            onClose={handleClosePO}
          />
        )}
      </PageHeader>
    </Layout>
  );
};

export default ReportsPage; 